import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { PopupService } from '../../shared/modal/popup.service';
import { InventoryContext } from '../../inventory-manager/inventory.context';
import { HttpResponse } from '@angular/common/http';
import { LotSearch } from './lot-search.model';
import { finalize } from 'rxjs/operators';
import { SearchResult } from '../../shared/search-result.model';
import { ITEMS_PER_PAGE } from '../../shared';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../shared/alert/alert.service';
import { VIEW_NAMES_PERMISSION } from '../../shared/auth/permissions';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { LotService } from '../../shared/inventory/service/lot.service';
import { Lot } from '../../shared/inventory/model/lot.model';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-merge-create-dialog',
    templateUrl: './lot-merge-dialog.component.html'
})
export class LotMergeDialogComponent implements OnInit, OnDestroy {
    VIEW_NAMES_PERMISSION = VIEW_NAMES_PERMISSION;

    lots: Lot[];
    selectedItem: Lot;
    filteredItems: any;
    queryCount: any;
    itemsPerPage: any;
    page: any;
    previousPage: any;
    isLoading: boolean;
    searchResultDbId: string;

    lotUUIDs: string[];

    subscriptionsManager = new SubscriptionsManager();

    constructor(
        public activeModal: NgbActiveModal,
        private jhiLanguageService: JhiLanguageService,
        private modalService: NgbModal,
        private inventoryContext: InventoryContext,
        private activatedRoute: ActivatedRoute,
        private alertService: AlertService,
        private eventManager: JhiEventManager,
        private translateService: TranslateService,
        private lotService: LotService
    ) {
        this.subscriptionsManager.add = this.activatedRoute.queryParams.subscribe((params) => {
            this.searchResultDbId = params.searchResultDbId;
        });

        this.itemsPerPage = ITEMS_PER_PAGE;
        this.page = 1;
        this.lotUUIDs = this.inventoryContext.itemIds;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.loadAll();
    }

    trackId(index: number, item: Lot) {
        return item.lotId;
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    loadAll() {
        this.isLoading = true;

        if (this.searchResultDbId) {
            this.getSearchResults(this.searchResultDbId);
        } else {
            const request = new LotSearch();
            request.lotUUIDs = this.inventoryContext.itemIds;
            this.search(request).then((searchId) => {
                this.getSearchResults(searchId);
            });
        }
    }

    getSearchResults(searchId) {
        this.subscriptionsManager.add = this.lotService.getSearchResults({
            searchRequestId: searchId,
            page: this.page - 1,
            size: this.itemsPerPage,
            sort: null
        }).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<Lot[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.loadAll();
        }
    }

    search(request: LotSearch): Promise<string> {
        return new Promise((resolve, reject) => {
            this.subscriptionsManager.add = this.lotService.search(request).subscribe((response) => {
                this.searchResultDbId = response;

                const searchResult = new SearchResult();
                searchResult.searchResultDbId = this.searchResultDbId;
                resolve(searchResult.searchResultDbId);
            }, (error) => reject(error));
            this.page = 1;
        });
    }

    private onSuccess(data, headers) {
        this.filteredItems = headers.get('X-Filtered-Count');
        this.queryCount = this.filteredItems;
        this.lots = data;
    }

    isSelected(lot: Lot) {
        return lot && this.selectedItem && this.selectedItem.lotUUID === lot.lotUUID;
    }

    select(lot: Lot) {
        this.selectedItem = lot;
    }

    merge() {
        this.confirm().then(() => {
            const lotUUIDs = this.searchResultDbId ? null : this.lotUUIDs;
            this.subscriptionsManager.add = this.lotService.mergeLot({
                lotUUIDToKeep: this.selectedItem.lotUUID,
                searchComposite: {
                    searchRequest: this.searchResultDbId,
                    itemIds: lotUUIDs
                }
            }).subscribe(
                (res: HttpResponse<any>) => this.onMergeSuccess('inventoryManagerApp.lot.merge.success')
            );
        });
    }

    private confirm(): Promise<any> {
        let message = '';
        if (this.selectedItem) {
            message +=
                '<p><strong><i>' +
                    this.translateService.instant('inventoryManagerApp.lot.merge.confirmation.info',
                        { param: this.selectedItem.stockId }) +
                '</i></strong></p>';
            message += '<p>' + this.translateService.instant('inventoryManagerApp.lot.merge.confirmation.question') + '</p>';
        } else {
            return Promise.resolve();
        }

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = message;
        confirmModalRef.componentInstance.title = this.translateService.instant('confirmation');
        return confirmModalRef.result;
    }

    private onMergeSuccess(message: any) {
        this.alertService.success(message);
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: 'OK' });
        this.activeModal.close(message);
    }

}

@Component({
    selector: 'jhi-lot-merge-popup',
    template: ''
})
export class LotMergePopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(
        private route: ActivatedRoute,
        private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(() => {
            this.popupService.open(LotMergeDialogComponent as Component);
        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }
}
